import { useCallback, useEffect, useState } from "react";

import isEmpty from "lodash/isEmpty";
import { useRouter } from "next/router";

import {
  setPinSearchFromUrl,
  setView
} from "@/redux/features/place-right-panel/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import {
  Box,
  Flex,
  Spinner,
  useDisclosure,
  useMediaQuery
} from "@/storybook-components";
import EsriProvider from "./context/esri-context";
import EsriMap from "./esri-map";
import CreateAndEditPin from "./esri-map/create-edit-pin/create-and-edit-pin";
import EsriSidePanel from "./esri-map/esri-right-panel";
import EsriPinDetails from "./esri-map/pin-details";
import ViewPinDrawer from "./esri-map/view-pin-drawer";

import useAccessStatus from "@/hooks/places/use-access-status";

import usePinView from "@/hooks/places/use-pin-view";

const Places = () => {
  const rightPanelProps = useDisclosure();
  const viewPinModalProps = useDisclosure();
  const router = useRouter();
  const [isPinClicked, setIsPinclicked] = useState(false);
  const [isLoading, setLoaderState] = useState(true);

  const { accessStatus } = useAccessStatus();
  const rightPanelView = useAppSelector(
    (state) => state.placesRightPanelView.view
  );
  const { login } = useAppSelector(
    (state) => state.user.registeredUser.profile
  );

  const heightOfMap = login ? "135px" : "127px";

  const navbarElement = document.getElementsByClassName(
    "ehq-participant-header"
  )[0];
  const height = navbarElement ? `calc(100vh - ${heightOfMap})` : "100vh";

  const categories = useAppSelector(
    (state) => state.markerCategories.categories
  );
  const [isDesktop] = useMediaQuery("(min-width: 768px)");
  const dispatch = useAppDispatch();

  const [symbols, setSymbols] = useState([]);
  const { viewMarkerDetailsScreen } = usePinView({
    viewPinModalProps,
    setIsPinclicked
  });
  const getSymbolsAndPreview = () => {
    if (isEmpty(categories)) return [];

    categories.map((category: any) => {
      if (category.visible) {
        setSymbols((prevSymbols) => [
          ...prevSymbols,
          {
            id: category.id,
            value: category.icon,
            name: category.name,
            color: category.color,
            category: { ...category }
          }
        ]);
      }
    });
  };

  // closes the drawer and shows the map in the mobile view
  const handleCloseDrawer = () => {
    rightPanelProps?.onClose();
  };
  const viewPinDetails = useAppSelector((state) => state.place.pinDetails);
  const {
    query: { marker }
  } = router;
  useEffect(() => {
    getSymbolsAndPreview();
    if (marker) {
      dispatch(setPinSearchFromUrl(true));
      !isEmpty(viewPinDetails) && dispatch(setView("view-pin-details"));
    } else {
      dispatch(setView("activity-feed"));
    }
  }, []);

  const handleRightPanelViewComponent = () => {
    switch (rightPanelView) {
      case "view-pin-details":
      case "activity-feed":
        return (
          <EsriPinDetails
            viewMarkerClickHandler={viewMarkerClickHandler}
            rightPanelProps={rightPanelProps}
          />
        );
      case "create-pin":
      case "edit-pin":
        return accessStatus ? (
          <CreateAndEditPin
            symbols={symbols}
            handleCloseDrawer={handleCloseDrawer}
          />
        ) : null;

      default:
        return null;
    }
  };

  useEffect(() => {
    hideLoader();
  }, []);

  const hideLoader = useCallback(() => {
    const timeoutId = setTimeout(() => {
      setLoaderState(false);
    }, 1000);

    // eslint-disable-next-line no-undef
    return () => clearTimeout(timeoutId); // Cleanup to clear the timeout on component unmount or dependency change
  }, []);

  const viewMarkerClickHandler = useCallback(
    async (data, cardDetails) => {
      viewMarkerDetailsScreen(data, cardDetails);
    },
    [isDesktop]
  );

  useEffect(() => {
    if (!viewPinModalProps.isOpen) {
      setIsPinclicked(false);
    }
  }, [viewPinModalProps.isOpen]);

  const placesComponent = useCallback(() => {
    return (
      <EsriProvider>
        <Flex direction="row">
          <EsriMap
            isLoading={isLoading}
            viewMarkerClickHandler={viewMarkerClickHandler}
            viewPinModalProps={viewPinModalProps}
          />

          {isLoading && (
            <Spinner size="xl" thickness="3px">
              <Box
                top="50%"
                left="40%"
                color="gray.100"
                height={height}
                width="100vw"
              />
            </Spinner>
          )}

          {!isLoading && (
            <EsriSidePanel
              onOpen={rightPanelProps.onOpen}
              handleCloseDrawer={handleCloseDrawer}
              isOpen={rightPanelProps.isOpen}
              symbols={symbols}
              rightPanelProps={rightPanelProps}
            >
              {handleRightPanelViewComponent()}
            </EsriSidePanel>
          )}
          {isPinClicked && (
            <>
              <ViewPinDrawer
                viewPinModalProps={viewPinModalProps}
                rightPanelProps={rightPanelProps}
              />
            </>
          )}
        </Flex>
      </EsriProvider>
    );
  }, [rightPanelProps, handleCloseDrawer, viewPinModalProps]);

  return placesComponent();
};

export default Places;
